import React from "react";
import styled from "@emotion/styled";
import {
  fontFamily,
  fontSize,
  fontWeight,
  spacing,
  borderRadius,
  border as borderToken,
  interactive,
  surface,
  text,
} from "@atlas-int/tokens";
import type { SubExport } from "../packageRegistry";

interface SubExportTableProps {
  packageName: string;
  subExports: SubExport[];
}

const Container = styled.div`
  margin-bottom: ${spacing[10]};
`;

const Title = styled.h3`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.semibold};
  color: ${text.primary};
  margin: 0 0 ${spacing[4]} 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${fontSize.sm};
`;

const Th = styled.th`
  text-align: left;
  padding: ${spacing[2]} ${spacing[4]};
  font-weight: ${fontWeight.semibold};
  color: ${text.secondary};
  border-bottom: 2px solid ${borderToken.default};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: ${spacing[2]} ${spacing[4]};
  color: ${text.primary};
  border-bottom: 1px solid ${borderToken.subtle};
  vertical-align: top;
`;

const PathCode = styled.code`
  font-family: ${fontFamily.mono};
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.semibold};
  color: ${interactive.default};
`;

const KeyExportBadge = styled.code`
  display: inline-block;
  font-family: ${fontFamily.mono};
  font-size: ${fontSize.xs};
  padding: ${spacing[1]} ${spacing[2]};
  margin: 1px ${spacing[1]};
  background-color: ${surface.muted};
  border-radius: ${borderRadius.base};
  color: ${text.secondary};
`;

const SubExportTable: React.FC<SubExportTableProps> = ({
  packageName,
  subExports,
}) => {
  return (
    <Container>
      <Title>Sub-exports</Title>
      <Table>
        <thead>
          <tr>
            <Th>Import Path</Th>
            <Th>Description</Th>
            <Th>Key Exports</Th>
          </tr>
        </thead>
        <tbody>
          {subExports.map((sub) => (
            <tr key={sub.path}>
              <Td>
                <PathCode>
                  {packageName}/{sub.path.replace("./", "")}
                </PathCode>
              </Td>
              <Td>{sub.description}</Td>
              <Td>
                {sub.keyExports.map((exp) => (
                  <KeyExportBadge key={exp}>{exp}</KeyExportBadge>
                ))}
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Container>
  );
};

export default SubExportTable;
